import React, { useEffect } from 'react';
import { graphql, withPrefix } from 'gatsby';
import LazyLoad from 'react-lazyload';
import { i18nFunct } from '@lucid/gatsby-theme-lucid';
import moment from 'moment';
import { Redirect } from '@reach/router';

// @ts-ignore
import Layout from '../../components/layout';
import Navbar, { navbarThemes } from '../../components/navbar/navbar';
import Footer from '../../components/footer/footer';
import LayoutContext, {
  LayoutContextProps
} from '../../contexts/layoutContext';
import EmailOnly, {
  EmailOnlyLayouts
} from '../../components/email-only/email-only';
import SocialProof from '../../components/social-proof/social-proof';
import FooterCard from '../../components/footer-card/footer-card';
import ValueProp from '../../components/value-prop/value-prop';

import './homepage.scss';

import AbTest, { PossibleTrigger, Variation } from '../../components/ab-test/ab-test';
import AbTestBranch from '../../components/ab-test-branch/ab-test-branch';
import DriftBot from '../../components/ab-tests/drift-bot/drift-bot';

import { ITemplateProps, ExtraData } from '../../types/types';
import useQueryParams from '../../hooks/use-query-params';

import {
  Drupal,
  FileConnection,
  ImageSharpConnection,
  Drupal_NodeHomepage,
  Drupal_ParagraphCta,
  Drupal_ParagraphMarketoForm,
  Drupal_FieldNodeHomepageFieldValuePropCard
} from '../../generated/graphql';

interface NavigatorLanguage extends Navigator {
  userLanguage?: string;
}

interface HomepageProps extends ITemplateProps {
  data: {
    drupal: Drupal;
    i18nData?: FileConnection;
    mobile: ImageSharpConnection;
    tablet: ImageSharpConnection;
  };
}

const Homepage = ({ data, pageContext, location }: HomepageProps) => {
  const { i18nData, mobile, tablet, drupal } = data;
  const [queryParams] = useQueryParams();
  const { langPrefix, pageType, nid, i18nKey, extraData } = pageContext;
  const node = drupal.nodeById as Drupal_NodeHomepage;

  const {
    marketoForms,
    otherTranslationData,
    navbarMenu,
    footerMenu,
    metatags
  } = JSON.parse(extraData) as ExtraData;

  const i18n = i18nFunct(i18nData, i18nKey);
  const isLangEnglish = langPrefix === 'en';

  const bgImageSrc =
    langPrefix === 'en'
      ? 'https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/Homepage/Conditional-formatting-hero2.png'
      : 'https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/hero-mockup2.jpg';

  const bgImageSrcWebp =
    langPrefix === 'en'
      ? 'https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/Homepage/Conditional-formatting-hero2.webp'
      : 'https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/Homepage/hero-mockup2.webp';

  const bgImageSrcWebpSmall =
    langPrefix === 'en'
      ? 'https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/Homepage/Conditional-formatting-hero2-small.webp'
      : 'https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/Homepage/hero-mockup2.webp';

  const checkForI18nRedirect = () => {
    if (!document.referrer && isLangEnglish) {
      const extendedNavigator = navigator as NavigatorLanguage;
      const preferredLanguage = extendedNavigator.languages
        ? extendedNavigator.languages[0]
        : extendedNavigator.language || extendedNavigator.userLanguage;
      if (preferredLanguage) {
        const normalizedLanguage = preferredLanguage.toLowerCase();
        const i18nRoute = otherTranslationData.find(route => {
          const routeId = route.entityLanguage!.id;
          return (
            routeId === normalizedLanguage ||
            routeId!.substring(0, 2) === normalizedLanguage.substring(0, 2)
          );
        });
        if (i18nRoute && i18nRoute.entityLanguage!.id !== langPrefix) {
          window.location.replace(i18nRoute.entityUrl!.path!);
        }
      }
    }
  };

  useEffect(() => {
    checkForI18nRedirect();
  }, []);

  const enterprseSection = node.fieldEnterpriseSection
    ? (node.fieldEnterpriseSection.entity as Drupal_ParagraphCta)
    : null;

  const enterpriseMarketoEntity = enterprseSection
    ? (enterprseSection.fieldMarketo!.entity as Drupal_ParagraphMarketoForm)
    : null;

  const content = (
    <>
      <Navbar
        menu={navbarMenu}
        theme={navbarThemes.white}
        scrollDistance={10}
        darkButtons={isLangEnglish}
        alternateButtonStyles={!isLangEnglish}
      />
      <LayoutContext.Consumer>
        {({ marketo }: LayoutContextProps) => (
          <main className="background--gray-pattern">
            <section className="hero">
              <div className="lucid-container  lucid-container--flex">
                <div className="card  card--raised  card--big  card--clear-image">
                  <h1 className="card--title">{node.fieldTopCardHeading}</h1>
                  <p className="margin-t-sm  margin-b-sm">
                    {node.fieldTopCardSubheading}
                  </p>
                  <EmailOnly
                    buttonText={node.fieldEmailOnlyButton!}
                    googleButton={node.fieldGoogleSsoButton!}
                    layout={
                      langPrefix === 'en'
                        ? EmailOnlyLayouts.Uneven
                        : EmailOnlyLayouts.Stacked
                    }
                    wideBreak
                    placeholder={i18n('mktg-abtest-hp-work-email')}
                  />
                </div>
                <div className="background-image">
                  <picture>
                    <source
                      srcSet={`${bgImageSrcWebpSmall} 1x, ${bgImageSrcWebp} 2x`}
                      type="image/webp"
                    />
                    <source srcSet={bgImageSrc} type="image/png" />
                    <img alt="Lucidchart Orgchart" src={bgImageSrc} />
                  </picture>
                </div>
              </div>
            </section>
            {node.fieldValueProp && (
              <ValueProp
                intro={node.fieldValueProp.entity as Drupal_ParagraphCta}
                cards={
                  node.fieldValuePropCard as Drupal_FieldNodeHomepageFieldValuePropCard[]
                }
                mobile={mobile}
                tablet={tablet}
                desktop={node.fieldHomepageChart}
              />
            )}

            <SocialProof>
              <div className="homepage__social-proof-container">
                <h2 className="homepage__social-proof-title">
                  {node.fieldSocialProofHeading}
                </h2>
                <p>{node.fieldSocialProofSubheading}</p>
              </div>
              <div className="homepage__social-proof-container homepage__social-proof-container--eo">
                <EmailOnly
                  buttonText={node.fieldEmailOnlyButton!}
                  layout={EmailOnlyLayouts.Cushy}
                  hideSSO
                  wideBreak
                  placeholder={i18n('mktg-abtest-hp-work-email')}
                />
              </div>
            </SocialProof>

            <section className="lucid-container  lucid-container--flex padding-b-sm-mobile padding-t-sm-mobile enterprise">
              <div className="enterprise-photo">
                <LazyLoad offset={100}>
                  <img
                    src="https://d2slcw3kip6qmk.cloudfront.net/marketing/pages/chart/Homepage/redesign/enterprise.jpg"
                    alt="Lucidchart at companies"
                  />
                </LazyLoad>
              </div>

              {enterprseSection && (
                <div className="enterprise-text">
                  <h2>{enterprseSection.fieldCtaHeading}</h2>
                  <p className="no-margin-baby-mobile">
                    {enterprseSection.fieldDescription}
                  </p>
                  {enterpriseMarketoEntity && (
                    <button
                      type="button"
                      className="button chart chart--secondary
                      margin-r-sm  js-homepage-enterprise"
                      onClick={() =>
                        marketo.getMarketoModal(
                          enterpriseMarketoEntity.fieldMarketoId!
                        )
                      }
                    >
                      {node.fieldEnterpriseFormText}
                    </button>
                  )}

                  {enterprseSection.fieldCtaLink && (
                    <a
                      href={enterprseSection.fieldCtaLink.url!.path!}
                      className="link--secondary  margin-t-sm-mobile"
                    >
                      {enterprseSection.fieldCtaLink.title}
                    </a>
                  )}
                </div>
              )}
            </section>

            {node.fieldFooterHeading && (
              <FooterCard>
                <div
                  className="padding-r-sm
                          padding-r-sm-tablet
                          no-padding-mobile
                          margin-b-sm-mobile
                          card__title"
                >
                  <h2>{node.fieldFooterHeading}</h2>
                </div>
                <div className="no-padding-mobile  card__cta">
                  <EmailOnly
                    buttonText={
                      node.fieldEmailOnlyButton || i18n('mktg-nav-sign-up-free')
                    }
                    googleButton={
                      node.fieldGoogleSsoButton ||
                      i18n('mktg_email_only_or_sign_up_with', {
                        service: 'Google'
                      })
                    }
                    layout={
                      langPrefix === 'en'
                        ? EmailOnlyLayouts.Cushy
                        : EmailOnlyLayouts.Stacked
                    }
                    wideBreak
                    hideOr
                    placeholder={i18n('mktg-abtest-hp-work-email')}
                  />
                </div>
              </FooterCard>
            )}
          </main>
        )}
      </LayoutContext.Consumer>
      <Footer
        menu={footerMenu}
        languageSwitcherData={otherTranslationData}
        padFooter
      />
      <AbTest test="driftBot8" trigger={PossibleTrigger.Delay} delay={41}>
        <AbTestBranch branch={Variation.B}>
          <DriftBot
            interaction={[
              {
                interactionId: 92016,
                schedule: {
                  days: [1, 2, 3, 4, 5],
                  startTime: moment({ hour: 7, minute: 0 }),
                  endTime: moment({ hour: 17, minute: 30 }),
                  timezone: 'America/Denver'
                }
              },
              {
                interactionId: 92896
              }
            ]}
            delay={40}
          />
        </AbTestBranch>
      </AbTest>
    </>
  );

  return (
    <Layout
      pageType={pageType}
      location={location}
      langPrefix={langPrefix}
      metatags={metatags}
      marketoForms={marketoForms}
      nid={nid}
      otherTranslationData={otherTranslationData}
    >
      <AbTest
        trigger={PossibleTrigger.Load}
        test="hpProduct2"
        languageTrigger={langPrefix === 'en'}
        aboveTheFold
      >
        <AbTestBranch branch={Variation.A}>{content}</AbTestBranch>
        <AbTestBranch branch={Variation.B}>{content}</AbTestBranch>
        <AbTestBranch branch={Variation.C}>
          <Redirect to={withPrefix(`/home${queryParams}`)} noThrow />
        </AbTestBranch>
      </AbTest>
    </Layout>
  );
};

export default Homepage;

export const HomepageQuery = graphql`
  query(
    $nid: String!
    $LanguageId: Drupal_LanguageId!
    $i18nKey: String!
    $langPrefix: String!
  ) {
    drupal {
      nodeById(id: $nid, language: $LanguageId) {
        ... on Drupal_NodeHomepage {
          fieldTopCardHeading
          fieldTopCardSubheading
          fieldEmailOnlyButton
          fieldGoogleSsoButton
          fieldValueProp {
            entity {
              ... on Drupal_ParagraphCta {
                fieldCtaHeading
                fieldCtaLink {
                  url {
                    path
                  }
                  title
                }
              }
            }
          }
          fieldValuePropCard {
            entity {
              ... on Drupal_ParagraphCta {
                fieldCtaHeading
                fieldDescription
                fieldCtaLink {
                  url {
                    path
                  }
                  title
                }
              }
            }
          }
          fieldSocialProofHeading
          fieldSocialProofSubheading
          fieldEnterpriseSection {
            entity {
              ... on Drupal_ParagraphCta {
                fieldCtaHeading
                fieldDescription
                fieldCtaLink {
                  url {
                    path
                  }
                  title
                }
                fieldMarketo {
                  entity {
                    ... on Drupal_ParagraphMarketoForm {
                      fieldMarketoId
                    }
                  }
                }
              }
            }
          }
          fieldEnterpriseFormText
          fieldFooterHeading
          fieldHomepageChart
        }
      }
    }

    mobile: allFile(
      filter: {
        sourceInstanceName: { eq: "homepage-images" }
        extension: { eq: "png" }
        relativeDirectory: { eq: $langPrefix }
        relativePath: { regex: "/mobile/" }
      }
      sort: { fields: [relativePath], order: ASC }
    ) {
      edges {
        node {
          childImageSharp {
            fluid(maxWidth: 600, quality: 100) {
              ...GatsbyImageSharpFluid_withWebp
            }
          }
          relativeDirectory
        }
      }
    }

    tablet: allFile(
      filter: {
        sourceInstanceName: { eq: "homepage-images" }
        extension: { eq: "png" }
        relativeDirectory: { eq: $langPrefix }
        relativePath: { regex: "/tablet/" }
      }
      sort: { fields: [relativePath], order: ASC }
    ) {
      edges {
        node {
          childImageSharp {
            fixed(width: 1021, height: 400, quality: 100) {
              ...GatsbyImageSharpFixed_withWebp
            }
          }
          relativeDirectory
        }
      }
    }

    i18nData: allFile(
      filter: {
        sourceInstanceName: { eq: "i18n" }
        name: { in: [$i18nKey, "en"] }
      }
    ) {
      edges {
        node {
          name
          childSrcJson {
            mktg_nav_sign_up_free
            mktg_email_only_or_sign_up_with
            mtkg_abtest_hp_social_wording
            mktg_abtest_hp_work_email
          }
        }
      }
    }
  }
`;
